import React, { type ReactNode, type Ref, useState } from "react";
import { RefreshControl, type ScrollView as ScrollViewNative, useWindowDimensions } from "react-native";
import type { GetProps } from "@medbillai/ui";
import { isWeb, ScrollView, type ScrollViewProps, styled, XStack, YStack } from "@medbillai/ui";
import { sentryService } from "../lib/sentry";
import { BackgroundRadialGrantedGradient } from "./BackgroundRadialGrantedGradient";
import { ShadowLoginBanner } from "./ShadowLoginBanner";
import { gradientBackgroundCircleScreenPadding } from "./screenPadding";
export { gradientBackgroundCircleScreenPadding, gradientBackgroundInfoScreenPadding } from "./screenPadding";

// This is a standardized component for wrapping screens in the
// application, so that we can have a consistent look and feel
// across the app.

const ScreenStack = styled(YStack, {
  name: "ScreenStack",
  p: "$3",
  gap: "$3",
  alignItems: "stretch",
  variants: {
    centerHorizontal: {
      true: {
        alignSelf: "center"
      }
    },
    centerVertical: {
      true: {
        justifyContent: "center"
      }
    },
    centerContent: {
      true: {
        alignItems: "center"
      }
    },
    extraPadding: {
      true: {
        p: "$6",
        $gtSm: {
          p: "$8"
        }
      }
    },
    noPadding: {
      true: {
        p: 0,
        $gtSm: {
          p: 0
        }
      }
    }
  } as const
});
type ScrollViewContainerStyle = Exclude<ScrollViewProps["contentContainerStyle"], "unset" | undefined>;
const scrollViewContainerStyle = (): ScrollViewContainerStyle => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  flexGrow: 1
});
export type ScreenViewProps = {
  children: ReactNode;
  disableScroll?: boolean;
  // More whitespace.
  extraPadding?: boolean;
  // Less whitespace.
  noPadding?: boolean;
  // Center the screen vertically and/or horizontally.
  centerVertical?: boolean;
  centerHorizontal?: boolean;
  // Center the content of the screen horizontally.
  centerContent?: boolean;
  // Cap the width of the screen (web only).
  widthLimit?: boolean | number;
  // Optional background color.
  backgroundColor?: ScrollViewProps["backgroundColor"];
  // Allow deep customization if needed.
  scrollViewProps?: ScrollViewProps & {
    ref?: Ref<ScrollViewNative | null>;
  };
  scrollViewContainerProps?: ScrollViewContainerStyle;
  stackProps?: React.ComponentProps<typeof ScreenStack>;
  // Additional content before or after the stack.
  renderBefore?: ReactNode;
  renderAfter?: ReactNode;
  // If paddingTop is set on the scrollViewContainerProps, this
  // can be used to offset the refresh control, ideally matching
  // the paddingTop value
  contentRefreshOffset?: number;
  // If provided, the screen will have a gradient applied to the top of the
  // screen, usually used with transparent headers and standard padding
  // applied to counteract the lack of header spacing
  backgroundRadialGradient?: boolean;
  // If provided, the screen will have a pull-to-refresh action.
  onRefresh?: () => Promise<unknown>;
  // Used to customize the background radial gradient if
  // backgroundRadialGradient is true
  backgroundRadialGradientProps?: GetProps<typeof BackgroundRadialGrantedGradient>;
};
export const defaultWidthLimit = 600;
export const ScreenView = ({
  children,
  disableScroll = false,
  extraPadding = false,
  noPadding = false,
  centerVertical = false,
  centerHorizontal = false,
  centerContent = false,
  widthLimit = true,
  backgroundColor,
  scrollViewProps,
  scrollViewContainerProps,
  stackProps,
  renderBefore,
  renderAfter,
  contentRefreshOffset = 0,
  backgroundRadialGradient = false,
  backgroundRadialGradientProps,
  onRefresh
}: ScreenViewProps) => {
  const {
    height,
    fontScale
  } = useWindowDimensions();
  // Default to scroll on larger screens to the parent prop. However on small
  // screens there are a lot of scenarios the simulator behaves differently than
  // the device, so we default to scroll on smaller screens to prevent blocking
  // users from accessing features.
  // We also force scroll on smaller screens if the font scale is larger than
  // 1.1 because we know there is accessibility settings enabled on the
  // application and cannot anticipate what the screen will render as.
  const forceScrollEnabled = height <= 800 || fontScale > 1.1;
  const [refreshing, setRefreshing] = useState(false);
  const doRefresh = async () => {
    if (onRefresh) {
      setRefreshing(true);
      try {
        await onRefresh();
      } catch (err) {
        sentryService.captureException(err as Error);
      } finally {
        setRefreshing(false);
      }
    }
  };
  let maxWidth: number | undefined;
  if (widthLimit) {
    if (typeof widthLimit === "number") {
      maxWidth = widthLimit;
    } else if (isWeb && widthLimit === true) {
      maxWidth = defaultWidthLimit;
    }
  }
  return <>
      {backgroundRadialGradient ? <BackgroundRadialGrantedGradient {...backgroundRadialGradientProps} /> : null}

      <ScrollView keyboardShouldPersistTaps="handled"
    // Remove disable scroll on smaller screens to allow for scrolling, this
    // is to make sure everything is accessible on smaller screens, that cant
    // be replicated in the simulator
    scrollEnabled={forceScrollEnabled ? true : !disableScroll} centerContent={centerVertical} contentContainerStyle={{
      ...scrollViewContainerStyle(),
      ...scrollViewContainerProps
    }} backgroundColor={backgroundColor} width="100%" fg={1} refreshControl={onRefresh ? <RefreshControl refreshing={refreshing} onRefresh={doRefresh} progressViewOffset={contentRefreshOffset} /> : undefined} {...scrollViewProps} data-sentry-element="ScrollView" data-sentry-source-file="ScreenView.tsx">
        {!isWeb ? <YStack fg={0}>
            <ShadowLoginBanner />
          </YStack> : null}
        {renderBefore ?? null}
        <ScreenStack centerHorizontal={centerHorizontal} centerVertical={centerVertical} centerContent={centerContent} extraPadding={extraPadding} noPadding={noPadding} width="100%" fg={1} maxWidth={maxWidth} backgroundColor={backgroundColor} {...stackProps} data-sentry-element="ScreenStack" data-sentry-source-file="ScreenView.tsx">
          {children}
        </ScreenStack>
        {renderAfter ? <ScreenStack maxWidth={maxWidth} width="100%" centerHorizontal={centerHorizontal} extraPadding={extraPadding} noPadding={noPadding} py="$0">
            {renderAfter}
          </ScreenStack> : null}
      </ScrollView>
    </>;
};

// Solo variants are for full-screen content that gets centered.
export const ScreenViewCenter = (props: ScreenViewProps) => {
  return <ScreenView centerVertical {...props} data-sentry-element="ScreenView" data-sentry-component="ScreenViewCenter" data-sentry-source-file="ScreenView.tsx" />;
};
export const ScreenViewTopThird = (props: ScreenViewProps) => {
  return <ScreenView renderBefore={<XStack flexGrow={1} flexShrink={1} maxHeight={100} />} stackProps={{
    flexGrow: 4,
    flexShrink: 0
  }} {...props} data-sentry-element="ScreenView" data-sentry-component="ScreenViewTopThird" data-sentry-source-file="ScreenView.tsx" />;
};
export const ScreenViewShadowCircle = (props: ScreenViewProps) => {
  const {
    stackProps,
    ...rest
  } = props;
  return <ScreenView backgroundRadialGradient centerHorizontal extraPadding disableScroll renderBefore={<XStack minHeight="10%" $short={{
    minHeight: "2%"
  }} />} stackProps={{
    px: "$8",
    gap: "$6",
    $short: {
      gap: "$2",
      px: "$4"
    },
    pt: gradientBackgroundCircleScreenPadding,
    ...stackProps
  }} {...rest} data-sentry-element="ScreenView" data-sentry-component="ScreenViewShadowCircle" data-sentry-source-file="ScreenView.tsx" />;
};
export const FlowTextFormScreenView = (props: ScreenViewProps) => {
  return <ScreenView disableScroll noPadding centerHorizontal stackProps={{
    flex: 1,
    paddingTop: "$2",
    paddingHorizontal: "$4.5"
  }} {...props} data-sentry-element="ScreenView" data-sentry-component="FlowTextFormScreenView" data-sentry-source-file="ScreenView.tsx" />;
};
export const ReleaseScreenView = ({
  includePadding = true,
  ...props
}: ScreenViewProps & {
  includePadding?: boolean;
}) => {
  return <ScreenView centerHorizontal stackProps={{
    flex: 1,
    paddingVertical: "$4.5",
    paddingHorizontal: includePadding ? "$4.5" : 0
  }} {...props} data-sentry-element="ScreenView" data-sentry-component="ReleaseScreenView" data-sentry-source-file="ScreenView.tsx" />;
};